import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveAPI, employeeAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Loader2, Check, X, Clock, CheckCircle2, XCircle } from 'lucide-react';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const LeaveManagement = () => {
  const { user } = useAuth();
  const { dialogProps, confirm } = useConfirmation();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [pendingHolidays, setPendingHolidays] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch leave types and all requests together; these are required for the page
      const [typesRes, allRes, pendingRes, pendingHolRes, allHolRes] = await Promise.all([
        leaveAPI.getTypes(),
        leaveAPI.getRequests({ page: 1, page_size: 50 }),
        leaveAPI.getPendingRequests(),
        leaveAPI.getPendingHolidayRequests(),
        leaveAPI.getHolidayRequests({ scope: 'all' })
      ]);
      setLeaveTypes(typesRes.data);
      setAllRequests(allRes.data);
      setPendingRequests(pendingRes.data);
      setPendingHolidays(pendingHolRes.data);
      setAllHolidays(allHolRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError(error.response.data.detail);
      } else {
        toast.error('Failed to fetch leave/holiday data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await leaveAPI.approveRequest(requestId);
      toast.success('Leave request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    const result = await confirm({
      title: 'Reject Leave Request',
      description: 'Are you sure you want to reject this leave request? You can optionally provide a reason.',
      confirmText: 'Reject',
      variant: 'destructive',
      hasPrompt: true,
      promptLabel: 'Rejection Reason (optional)',
      promptPlaceholder: 'Enter reason for rejection...',
    });
    if (!result.confirmed) return;

    try {
      await leaveAPI.rejectRequest(requestId, result.value);
      toast.success('Leave request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleApproveHoliday = async (requestId) => {
    try {
      await leaveAPI.approveHolidayRequest(requestId);
      toast.success('Optional holiday request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve holiday request');
    }
  };

  const handleRejectHoliday = async (requestId) => {
    const confirmed = await confirm({
      title: 'Reject Holiday Request',
      description: 'Are you sure you want to reject this optional holiday request?',
      confirmText: 'Reject',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await leaveAPI.rejectHolidayRequest(requestId);
      toast.success('Optional holiday request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject holiday request');
    }
  };

  const handleCancelHoliday = async (requestId) => {
    const confirmed = await confirm({
      title: 'Cancel Holiday Request',
      description: 'Are you sure you want to cancel this optional holiday request?',
      confirmText: 'Cancel Request',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await leaveAPI.cancelHolidayRequest(requestId);
      toast.success('Optional holiday request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel holiday request');
    }
  };

  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
    APPROVED_BY_MANAGER: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    APPROVED: 'bg-green-100 text-green-800 hover:bg-green-100',
    REJECTED: 'bg-red-100 text-red-800 hover:bg-red-100',
    CANCELLED: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  };

  const holidayStatusBadges = {
    PENDING: <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 flex gap-1 items-center w-fit"><Clock className="h-3 w-3" /> Pending</Badge>,
    APPROVED: <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex gap-1 items-center w-fit"><CheckCircle2 className="h-3 w-3" /> Approved</Badge>,
    REJECTED: <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex gap-1 items-center w-fit"><XCircle className="h-3 w-3" /> Rejected</Badge>,
    CANCELLED: <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100 flex gap-1 items-center w-fit">Cancelled</Badge>,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ModuleDisabled moduleName="leave" />;
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Leave Management</h1>
        <p className="text-muted-foreground text-sm sm:text-base">Manage leave requests, holiday requests, and types</p>
      </div>

      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="w-full sm:w-auto flex-wrap h-auto gap-1">
          <TabsTrigger value="pending">
            Pending Leaves
            {pendingRequests.length > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="optional-holidays">
            Optional Holiday Requests
            {pendingHolidays.length > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingHolidays.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="all">All Leaves</TabsTrigger>
          <TabsTrigger value="types">Leave Types</TabsTrigger>
        </TabsList>

        {/* ── Pending Leave Requests ── */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Leave Requests</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                        No pending leave requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingRequests.map((request) => (
                      <TableRow key={request.id} data-testid={`leave-request-${request.id}`}>
                        <TableCell className="font-medium">{request.employee_name}</TableCell>
                        <TableCell>{request.leave_type_name}</TableCell>
                        <TableCell>{new Date(request.start_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(request.end_date).toLocaleDateString()}</TableCell>
                        <TableCell>{request.days_count}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[request.status]}>
                            {request.status.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{request.reason || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleApprove(request.id)}
                              data-testid={`approve-${request.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleReject(request.id)}
                              data-testid={`reject-${request.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Optional Holidays Requests ── */}
        <TabsContent value="optional-holidays">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Pending Holiday Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Holiday Name</TableHead>
                      <TableHead>Holiday Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingHolidays.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                          No pending optional holiday requests
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingHolidays.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.holiday_name}</TableCell>
                          <TableCell>{req.holiday_date ? new Date(req.holiday_date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>{holidayStatusBadges[req.status]}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApproveHoliday(req.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleRejectHoliday(req.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>All Holiday Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Holiday Name</TableHead>
                      <TableHead>Holiday Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applied On</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allHolidays.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                          No holiday requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      allHolidays.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.holiday_name}</TableCell>
                          <TableCell>{req.holiday_date ? new Date(req.holiday_date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>{holidayStatusBadges[req.status]}</TableCell>
                          <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            {['PENDING', 'APPROVED'].includes(req.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs h-8"
                                onClick={() => handleCancelHoliday(req.id)}
                              >
                                Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── All Leave Requests ── */}
        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle>All Leave Requests</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applied On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                        No leave requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">{request.employee_name}</TableCell>
                        <TableCell>{request.leave_type_name}</TableCell>
                        <TableCell>{new Date(request.start_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(request.end_date).toLocaleDateString()}</TableCell>
                        <TableCell>{request.days_count}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[request.status]}>
                            {request.status.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Leave Types ── */}
        <TabsContent value="types">
          <Card>
            <CardHeader>
              <CardTitle>Leave Types</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Days Allowed</TableHead>
                    <TableHead>Carry Forward</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaveTypes.map((type) => (
                    <TableRow key={type.id}>
                      <TableCell className="font-medium">{type.name}</TableCell>
                      <TableCell className="font-mono">{type.code}</TableCell>
                      <TableCell>{type.days_allowed}</TableCell>
                      <TableCell>
                        {type.carry_forward ? `Yes (Max ${type.max_carry_forward_days})` : 'No'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={type.is_paid ? 'default' : 'secondary'}>
                          {type.is_paid ? 'Paid' : 'Unpaid'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={type.is_active ? 'default' : 'secondary'}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default LeaveManagement;
